import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { userInformationAtom } from '../../Services/_states';
import { isPlatformSuperAdmin } from '../../utils/isPlatformSuperAdmin.ts';
import { WIZARD_CAMPAIGN_GOALS } from '../../features/onboarding/utils/campaignGoals';
import { useBusinessTypeLookups } from '../../features/onboarding/hooks/useBusinessTypeLookups';
import {
  createCampaignTemplate,
  deleteCampaignTemplate,
  listCampaignTemplates,
  updateCampaignTemplate,
} from '../../features/onboarding/services/templateManageService';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';

interface FormFieldRow {
  id: string;
  alias_name?: string;
  key_name?: string;
  key_type?: string;
  is_required?: boolean;
  label?: string;
  type?: string;
  required?: boolean;
}

interface TemplateManageRow {
  id: number;
  business_type_id: number;
  business_type_name: string;
  goal: string;
  goal_label: string;
  sample_campaign_name: string;
  sample_form_fields: Array<{ id: string; label: string; type: string; required: boolean }>;
  is_active: boolean;
}

const AttributeTypes = [
  { value: 'STRING', label: 'String' },
  { value: 'INT', label: 'Int' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PASSWORD', label: 'Password' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'ENTITY', label: 'Entity' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'TEXTAREA', label: 'Textarea' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'FLOAT', label: 'Float' },
  { value: 'DATE', label: 'Date' },
  { value: 'FILE', label: 'File' },
  { value: 'TIME', label: 'Time' },
  { value: 'CITY', label: 'City' },
  { value: 'PINCODE', label: 'Pincode' },
  { value: 'CUSTOM', label: 'Custom' },
];

function newFieldId() {
  return crypto.randomUUID?.() ?? String(Date.now() + Math.random());
}

const emptyForm = () => ({
  business_type_id: '',
  goal: 'lead_generation',
  sample_campaign_name: '',
  form_fields: [] as FormFieldRow[],
  is_active: true,
});

export default function CampaignTemplatesTab() {
  const navigate = useNavigate();
  const user = useRecoilValue(userInformationAtom);
  const { showSnackBar } = useSnackBar();
  const { businessTypes, loading: typesLoading } = useBusinessTypeLookups();

  const [items, setItems] = useState < TemplateManageRow[] > ([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterGoal, setFilterGoal] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState < TemplateManageRow | null > (null);
  const [form, setForm] = useState(emptyForm());
  const [newField, setNewField] = useState({ alias_name: '', key_type: 'STRING', is_required: false });
  const [saving, setSaving] = useState(false);

  const allowed = isPlatformSuperAdmin(user);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const res = await listCampaignTemplates({
        business_type_id: filterType || undefined,
        goal: filterGoal || undefined,
      });
      if (res.ok) {
        setItems(res.items);
      } else {
        showSnackBar('Failed to load templates', 'error');
      }
    } catch {
      showSnackBar('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [allowed, filterType, filterGoal, showSnackBar]);

  useEffect(() => {
    if (!allowed) {
      navigate('/r/dashboard', { replace: true });
      return;
    }
    void load();
  }, [allowed, load, navigate]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row: TemplateManageRow) => {
    setEditing(row);
    setForm({
      business_type_id: String(row.business_type_id),
      goal: row.goal,
      sample_campaign_name: row.sample_campaign_name,
      form_fields: row.sample_form_fields.map((f) => ({ ...f })),
      is_active: row.is_active,
    });
    setDialogOpen(true);
  };

  const addField = () => {
    if (!newField.alias_name.trim()) return;
    const label = newField.alias_name.trim();
    setForm((prev) => ({
      ...prev,
      form_fields: [
        ...prev.form_fields,
        {
          id: newFieldId(),
          alias_name: label,
          key_name: label.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase().replace(/(^_|_$)/g, '') || `field_${prev.form_fields.length}`,
          key_type: newField.key_type,
          is_required: newField.is_required,
        },
      ],
    }));
    setNewField({ alias_name: '', key_type: 'STRING', is_required: false });
  };

  const removeField = (id: string) => {
    setForm((prev) => ({
      ...prev,
      form_fields: prev.form_fields.filter((f) => f.id !== id),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        business_type_id: Number(form.business_type_id),
        goal: form.goal,
        sample_campaign_name: form.sample_campaign_name,
        sample_form_fields: form.form_fields,
        is_active: form.is_active,
      };
      const res = editing
        ? await updateCampaignTemplate(editing.id, {
          sample_campaign_name: payload.sample_campaign_name,
          sample_form_fields: payload.sample_form_fields,
          is_active: payload.is_active,
        })
        : await createCampaignTemplate(payload);
      if (res.ok) {
        showSnackBar(editing ? 'Template updated' : 'Template created', 'success');
        setDialogOpen(false);
        void load();
      } else {
        const err = res.data as Record<string, string> | undefined;
        const msg =
          err && typeof err === 'object'
            ? Object.values(err).join(' ')
            : 'Could not save template';
        showSnackBar(msg, 'error');
      }
    } catch {
      showSnackBar('Could not save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: TemplateManageRow) => {
    if (!window.confirm(`Deactivate template for ${row.goal_label}?`)) return;
    const res = await deleteCampaignTemplate(row.id);
    if (res.ok) {
      showSnackBar('Template deactivated', 'success');
      void load();
    } else {
      showSnackBar('Could not deactivate template', 'error');
    }
  };

  if (!allowed) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Access denied. Platform super admin only.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Campaign Templates</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add template
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sample campaign names and form fields per business type and goal (used in Setup Wizard Step 5–6).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          select
          label="Business type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          <MenuItem value="">All</MenuItem>
          {businessTypes.map((bt) => (
            <MenuItem key={bt.id} value={String(bt.id)}>
              {bt.business_type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Goal"
          value={filterGoal}
          onChange={(e) => setFilterGoal(e.target.value)}
          sx={{ minWidth: 200 }}
          size="small"
        >
          <MenuItem value="">All</MenuItem>
          {WIZARD_CAMPAIGN_GOALS.map((g) => (
            <MenuItem key={g.value} value={g.value}>
              {g.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {loading || typesLoading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Business type</TableCell>
              <TableCell>Goal</TableCell>
              <TableCell>Sample campaign name</TableCell>
              <TableCell>Fields</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.business_type_name}</TableCell>
                <TableCell>{row.goal_label}</TableCell>
                <TableCell>{row.sample_campaign_name}</TableCell>
                <TableCell>{row.sample_form_fields.length}</TableCell>
                <TableCell>
                  <Chip
                    label={row.is_active ? 'Active' : 'Inactive'}
                    color={row.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(row)}
                    aria-label="Deactivate"
                    disabled={!row.is_active}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No templates. Add one or run seed command on server.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit template' : 'Add template'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Business type"
            value={form.business_type_id}
            onChange={(e) => setForm((p) => ({ ...p, business_type_id: e.target.value }))}
            disabled={!!editing}
            required
          >
            {businessTypes.map((bt) => (
              <MenuItem key={bt.id} value={String(bt.id)}>
                {bt.business_type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Campaign goal"
            value={form.goal}
            onChange={(e) => setForm((p) => ({ ...p, goal: e.target.value }))}
            disabled={!!editing}
            required
          >
            {WIZARD_CAMPAIGN_GOALS.map((g) => (
              <MenuItem key={g.value} value={g.value}>
                {g.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            margin="normal"
            label="Sample campaign name"
            value={form.sample_campaign_name}
            onChange={(e) => setForm((p) => ({ ...p, sample_campaign_name: e.target.value }))}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            }
            label="Active"
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Form fields
          </Typography>
          <List dense>
            {form.form_fields.map((f) => (
              <ListItem
                key={f.id}
                secondaryAction={
                  <IconButton edge="end" onClick={() => removeField(f.id)} size="small">
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={f.alias_name || f.label}
                  secondary={`${f.key_type || f.type}${f.is_required || f.required ? ' · Required' : ''}`}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <TextField
              label="Field label"
              size="small"
              value={newField.alias_name}
              onChange={(e) => setNewField({ ...newField, alias_name: e.target.value })}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={newField.key_type}
              onChange={(e) => setNewField({ ...newField, key_type: e.target.value })}
              sx={{ minWidth: 120 }}
            >
              {AttributeTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={newField.is_required}
                  onChange={(e) => setNewField({ ...newField, is_required: e.target.checked })}
                />
              }
              label={newField.is_required ? "Required" : "Optional"}
            />
            <Button variant="outlined" size="small" onClick={addField}>
              Add field
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
